import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Eye, MessageSquareText, Volume2 } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O1 · Conduite commentée vers l'avant
// Œuvre pédagogique interactive — vue subjective (conducteur) d'une route :
//  · 3 bandes de profondeur : LOIN / MOYEN / PROCHE (couleurs)
//  · 8 hotspots interactifs (piéton, feu, panneau, camion, vélo, enfant…)
//  · Mode « scan auto » : parcourt les zones selon la règle Loin→Moyen→Proche
//  · Cône du regard qui se déplace + label parlé (« Je vois… », « J'anticipe… »)
//  · Panneau latéral : phrase-type à dire, geste correct, danger à anticiper
// ══════════════════════════════════════════════════════════════════════════

const HOTSPOTS = [
  {
    id: 'panneau-stop',
    label: 'Panneau STOP',
    zone: 'loin',
    x: 620, y: 160, r: 22,
    icon: 'S',
    color: '#ef4444',
    phrase: "« Panneau STOP dans 200 m. Je prépare mon ralentissement. »",
    danger: "Un STOP anticipé de loin = freinage progressif, jamais brutal.",
    geste: "Lever le pied, rétrograder, contrôler rétros, cadencer l'approche.",
  },
  {
    id: 'feu-rouge',
    label: 'Feu tricolore',
    zone: 'loin',
    x: 480, y: 145, r: 18,
    icon: 'F',
    color: '#dc2626',
    phrase: "« Feu au vert au loin — je surveille sa couleur, il peut passer à l'orange. »",
    danger: "Un feu vert lointain risque de tourner au moment où tu arrives.",
    geste: "Anticiper : lever le pied si la file devant ralentit, jamais accélérer pour « passer ».",
  },
  {
    id: 'camion',
    label: 'Camion devant',
    zone: 'moyen',
    x: 400, y: 240, r: 32,
    icon: 'C',
    color: '#f59e0b',
    phrase: "« Camion devant à 80 m — je conserve mes distances de sécurité. »",
    danger: "Un camion masque la visibilité et freine plus lentement en cas d'urgence.",
    geste: "Rester à ≥ 3 secondes derrière. Ne pas coller. Envisager un dépassement seulement si nécessaire.",
  },
  {
    id: 'pieton',
    label: 'Piéton sur trottoir',
    zone: 'moyen',
    x: 200, y: 320, r: 26,
    icon: 'P',
    color: '#8b5cf6',
    phrase: "« Piéton sur ma droite qui approche du passage — je lève le pied. »",
    danger: "Un adulte peut traverser sans regarder. Un enfant, encore plus imprévisible.",
    geste: "Lever le pied, main prête sur le frein, être visible (feux de croisement).",
  },
  {
    id: 'velo',
    label: 'Cycliste',
    zone: 'moyen',
    x: 560, y: 300, r: 24,
    icon: 'V',
    color: '#10b981',
    phrase: "« Cycliste à droite en aval — je prévois 1,50 m d'écart pour le dépasser. »",
    danger: "Un cycliste peut zigzaguer (nid-de-poule, portière ouverte…).",
    geste: "Contrôle rétro gauche, clignotant, dépassement large. Jamais raser.",
  },
  {
    id: 'enfant',
    label: 'Enfant',
    zone: 'proche',
    x: 130, y: 440, r: 22,
    icon: 'E',
    color: '#ec4899',
    phrase: "« Enfant sur le trottoir — je ralentis franchement, il peut surgir. »",
    danger: "Un enfant < 10 ans a une perception du danger très partielle.",
    geste: "Ralentir MASSIVEMENT (30 km/h max en zone d'enfants). Prêt à freiner d'urgence.",
  },
  {
    id: 'nid',
    label: 'Nid-de-poule',
    zone: 'proche',
    x: 460, y: 470, r: 20,
    icon: 'N',
    color: '#a3a3a3',
    phrase: "« Nid-de-poule sur ma trajectoire — je le contourne légèrement à gauche. »",
    danger: "Passer dedans = suspension abîmée, jante voilée, pneu éclaté.",
    geste: "Contrôle rétro gauche AVANT tout écart. Contournement progressif.",
  },
  {
    id: 'obstacle',
    label: 'Voiture stationnée',
    zone: 'proche',
    x: 650, y: 460, r: 26,
    icon: 'O',
    color: '#f97316',
    phrase: "« Voiture garée à droite — je m'écarte, une portière peut s'ouvrir. »",
    danger: "Portière + piéton derrière la voiture stationnée = 2 dangers cumulés.",
    geste: "Écarter la trajectoire d'au moins 1 m. Contrôle rétro, allure adaptée.",
  },
];

// Ordre du « scan » : loin → moyen → proche, gauche → centre → droite
const SCAN_ORDER = ['panneau-stop', 'feu-rouge', 'camion', 'velo', 'pieton', 'enfant', 'obstacle', 'nid'];

const ZONES = [
  { key: 'loin',   label: 'LOIN (200 m+)',       yStart: 100, yEnd: 200, color: '#0ea5e9' },
  { key: 'moyen',  label: 'MOYEN (50-100 m)',    yStart: 200, yEnd: 380, color: '#f59e0b' },
  { key: 'proche', label: 'PROCHE (0-30 m)',     yStart: 380, yEnd: 560, color: '#ef4444' },
];

export default function ConduiteCommenteeSvg() {
  const [active, setActive] = useState(null); // id
  const [showZones, setShowZones] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanIdx, setScanIdx] = useState(0);
  const timerRef = useRef(null);

  // Scan automatique
  useEffect(() => {
    if (!scanning) return undefined;
    timerRef.current = setInterval(() => {
      setScanIdx((prev) => {
        const next = (prev + 1) % SCAN_ORDER.length;
        setActive(SCAN_ORDER[next]);
        return next;
      });
    }, 2400);
    return () => clearInterval(timerRef.current);
  }, [scanning]);

  const startScan = () => {
    setScanIdx(0);
    setActive(SCAN_ORDER[0]);
    setScanning(true);
  };
  const stopScan = () => setScanning(false);
  const reset = () => {
    setScanning(false);
    setActive(null);
    setScanIdx(0);
  };

  const activeSpot = HOTSPOTS.find((h) => h.id === active) || null;

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {!scanning ? (
          <button
            type="button"
            onClick={startScan}
            data-testid="o1-scan-start"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-cyan-500 text-cyan-950 font-bold text-[12px] hover:bg-cyan-400"
          >
            <Play size={13} /> Lancer le scan visuel
          </button>
        ) : (
          <button
            type="button"
            onClick={stopScan}
            data-testid="o1-scan-stop"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-500 text-amber-950 font-bold text-[12px] hover:bg-amber-400"
          >
            <Pause size={13} /> Pause
          </button>
        )}
        <button
          type="button"
          onClick={reset}
          data-testid="o1-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <button
          type="button"
          onClick={() => setShowZones((s) => !s)}
          data-testid="o1-toggle-zones"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[12px] font-semibold ${
            showZones ? 'bg-white/10 border-white/30 text-white' : 'border-white/15 text-white/60'
          }`}
        >
          <Eye size={13} /> Zones de profondeur
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* Vue subjective route */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Vue subjective d'une route à commenter">
            <defs>
              <linearGradient id="o1-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e3a5f" />
                <stop offset="100%" stopColor="#3b5f80" />
              </linearGradient>
              <linearGradient id="o1-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#0f172a" />
              </linearGradient>
              <radialGradient id="o1-glow" cx="50%" cy="50%">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.6" />
                <stop offset="60%" stopColor="#fbbf24" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Ciel */}
            <rect x={0} y={0} width={800} height={100} fill="url(#o1-sky)" />
            {/* Route en perspective (trapèze) */}
            <polygon points="0,560 800,560 560,100 240,100" fill="url(#o1-road)" />
            {/* Bandes latérales trottoirs */}
            <polygon points="0,560 240,100 220,100 0,540" fill="#475569" opacity={0.6} />
            <polygon points="800,560 560,100 580,100 800,540" fill="#475569" opacity={0.6} />
            {/* Ligne médiane centrale (pointillés perspective) */}
            <line x1={400} y1={100} x2={400} y2={560} stroke="#fbbf24" strokeWidth={3} strokeDasharray="14 10" opacity={0.75} />

            {/* Zones de profondeur */}
            {showZones && ZONES.map((z, i) => (
              <g key={z.key}>
                <rect
                  x={0} y={z.yStart} width={800} height={z.yEnd - z.yStart}
                  fill={z.color} opacity={0.06}
                />
                <line x1={0} y1={z.yEnd} x2={800} y2={z.yEnd} stroke={z.color} strokeWidth={1} strokeDasharray="6 4" opacity={0.4} />
                <text
                  x={30} y={z.yStart + 16}
                  fill={z.color}
                  fontSize={10}
                  fontWeight="bold"
                  fontFamily="monospace"
                  opacity={0.85}
                >
                  {z.label}
                </text>
              </g>
            ))}

            {/* Halo autour du hotspot actif */}
            {activeSpot && (
              <g>
                <circle cx={activeSpot.x} cy={activeSpot.y} r={70} fill="url(#o1-glow)">
                  <animate attributeName="r" values="55;80;55" dur="2s" repeatCount="indefinite" />
                </circle>
                {/* Cône du regard depuis le conducteur (bas centre) */}
                <line
                  x1={400} y1={600}
                  x2={activeSpot.x} y2={activeSpot.y}
                  stroke="#fbbf24"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  opacity={0.6}
                />
              </g>
            )}

            {/* Hotspots */}
            {HOTSPOTS.map((h) => {
              const isActive = h.id === active;
              return (
                <g
                  key={h.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setScanning(false); setActive(h.id); }}
                  data-testid={`o1-hotspot-${h.id}`}
                >
                  <circle
                    cx={h.x} cy={h.y} r={h.r}
                    fill={h.color}
                    fillOpacity={isActive ? 0.95 : 0.7}
                    stroke="#fff"
                    strokeWidth={isActive ? 3 : 1.5}
                  />
                  <text
                    x={h.x} y={h.y + 5}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={14}
                    fontWeight="900"
                  >
                    {h.icon}
                  </text>
                  {isActive && (
                    <text
                      x={h.x} y={h.y - h.r - 8}
                      textAnchor="middle"
                      fill="#fbbf24"
                      fontSize={11}
                      fontWeight="bold"
                    >
                      {h.label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Volant/tableau de bord stylisé (bas) */}
            <rect x={0} y={560} width={800} height={60} fill="#0b1120" />
            <path
              d="M 250 610 Q 400 570 550 610 L 550 620 L 250 620 Z"
              fill="#1e293b"
              stroke="#334155"
              strokeWidth={2}
            />
            <circle cx={400} cy={605} r={22} fill="none" stroke="#475569" strokeWidth={3} />
            <text x={400} y={609} textAnchor="middle" fill="#475569" fontSize={10} fontWeight="bold">CV</text>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.55} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">O1 · Conduite commentée vers l&apos;avant</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C2a — Prise d&apos;information dynamique</text>
            </g>
          </svg>

          {/* Indicateur de commentaire */}
          <div className="p-4 border-t border-white/10 bg-black/40">
            <div className="flex items-center gap-2 mb-1.5">
              <Volume2 size={14} className="text-cyan-300" />
              <span className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                Ce que tu dois dire à voix haute
              </span>
            </div>
            <div className="min-h-[52px] rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3">
              {activeSpot ? (
                <p className="text-cyan-100 text-[13.5px] italic leading-relaxed">
                  {activeSpot.phrase}
                </p>
              ) : (
                <p className="text-white/40 text-[12.5px]">
                  Clique sur un objet de la route ou lance le scan pour découvrir la phrase à dire.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o1-panel">
          <div className="rounded-xl border border-cyan-500/40 bg-cyan-950/20 p-3.5">
            <div className="flex items-center gap-2 text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <MessageSquareText size={13} /> Règle d&apos;or
            </div>
            <p className="mt-1.5 text-white/85 text-[12.5px] leading-relaxed">
              Commente à voix haute : <span className="text-cyan-200 font-semibold">LOIN → MOYEN → PROCHE</span>. Toujours dans cet ordre, toutes les <span className="text-cyan-200 font-semibold">3 à 5 secondes</span>.
            </p>
          </div>

          {activeSpot ? (
            <>
              <div className="mt-4 rounded-lg border p-3" style={{ borderColor: activeSpot.color + '60', background: activeSpot.color + '15' }}>
                <div className="flex items-center justify-between">
                  <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: activeSpot.color }}>
                    Objet identifié
                  </div>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-black text-[13px]" style={{ background: activeSpot.color }}>
                    {activeSpot.icon}
                  </div>
                </div>
                <div className="mt-1 font-serif-editorial text-white text-[18px] leading-tight">
                  {activeSpot.label}
                </div>
                <div className="mt-0.5 text-white/50 text-[11px] font-mono-spec uppercase tracking-wider">
                  Zone : {activeSpot.zone}
                </div>
              </div>

              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-950/15 p-3">
                <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Danger anticipé</div>
                <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{activeSpot.danger}</p>
              </div>

              <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3">
                <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
                <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{activeSpot.geste}</p>
              </div>
            </>
          ) : (
            <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-white/60 text-[12.5px] leading-relaxed">
              <span className="text-white/40 text-[10.5px] font-mono-spec uppercase tracking-wider block mb-1">Prêt à commencer ?</span>
              Clique sur <span className="text-cyan-300 font-semibold">Lancer le scan visuel</span> — les objets s&apos;éclaireront dans l&apos;ordre pédagogique. Ou clique manuellement sur un objet pour l&apos;analyser.
            </div>
          )}

          <div className="mt-4 grid grid-cols-3 gap-1.5 text-center">
            {ZONES.map((z) => (
              <div key={z.key} className="rounded-md py-1.5" style={{ background: z.color + '20', border: `1px solid ${z.color}50` }}>
                <div className="text-[9.5px] font-mono-spec uppercase font-bold" style={{ color: z.color }}>{z.key}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
